import {
  CSSProperties,
  ForwardRefExoticComponent,
  PropsWithChildren,
  ReactNode,
  RefAttributes,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { Variants, motion } from 'motion/react'

import { getPortalRoot } from '../../../config/portals'
import { usePopupPosition } from '../../../hooks/usePopupPosition'
import { Direction } from '../../common/types'

import styles from './Tooltip.module.scss'

interface TooltipProps {
  content?: ReactNode
  direction?: Direction
  autoHide?: number
  disabled?: boolean
  className?: string
  isNew?: boolean
  isMenu?: boolean
  wrapperClassName?: string
  style?: CSSProperties
  dataAttrTooltipContent?: string
}

export type UseImperativeHandleRef = { activate: () => void }

interface TooltipComponent
  extends ForwardRefExoticComponent<
    PropsWithChildren<TooltipProps> & RefAttributes<UseImperativeHandleRef>
  > {
  New: ForwardRefExoticComponent<
    PropsWithChildren<TooltipProps> & RefAttributes<UseImperativeHandleRef>
  >
  Menu: ForwardRefExoticComponent<
    PropsWithChildren<TooltipProps> & RefAttributes<UseImperativeHandleRef>
  >
}

export const Tooltip = forwardRef<UseImperativeHandleRef, PropsWithChildren<TooltipProps>>(
  (props, ref) => {
    const {
      content,
      direction = 'top',
      children,
      disabled,
      className,
      wrapperClassName,
      style,
      isMenu,
      isNew,
      autoHide,
      dataAttrTooltipContent,
    } = props

    const tooltipRoot = getPortalRoot('TOOLTIP')

    const triggerRef = useRef<HTMLDivElement>(null)
    const contentWrapperRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const { height, width } = contentRef?.current?.getBoundingClientRect() ?? {
      height: 0,
      width: 0,
    }

    const [active, setActive] = useState(false)

    const { contentOrigin } = usePopupPosition({
      boxRef: contentWrapperRef,
      contentDirection: direction,
      isOpen: active,
      triggerRef: triggerRef,
      distance: isNew ? '0.5rem' : '10px',
    })

    const variants = useMemo<Variants>(() => {
      return {
        open: {
          opacity: 1,
          transition: {
            delay: isNew ? 0.1 : 0,
            type: 'spring',
            bounce: 0,
            duration: 0.5,
          },
        },
        closed: {
          opacity: 0,
          transition: {
            type: 'spring',
            bounce: 0,
            duration: 0.25,
          },
        },
      }
    }, [isNew])

    useImperativeHandle(ref, () => ({
      activate() {
        setActive(true)

        if (autoHide) {
          setTimeout(() => setActive(false), autoHide)
        }
      },
    }))

    const showTip = () => {
      if (disabled || !content) {
        return
      }
      setActive(true)
    }

    const hideTip = () => {
      if (disabled || !content) {
        return
      }
      setActive(false)
    }

    return (
      <div
        onMouseEnter={showTip}
        onTouchStart={showTip}
        onMouseLeave={hideTip}
        className={classNames(
          styles.tooltipWrapper,
          { [styles.new]: isNew, [styles.menu]: isMenu },
          wrapperClassName
        )}
        ref={triggerRef}
        style={style}
      >
        {children}
        {createPortal(
          <motion.div
            animate={active ? 'open' : 'closed'}
            initial='closed'
            variants={variants}
            className={classNames(styles.tooltip, {
              [styles.active]: active,
              [styles.new]: isNew,
              [styles.menu]: isMenu,
            })}
            style={{
              top: contentOrigin?.top,
              left: contentOrigin?.left,
              transform: contentOrigin?.transform,
              minHeight: height,
              minWidth: width,
            }}
            ref={contentWrapperRef}
          >
            <div
              className={classNames(styles.content, className)}
              data-tooltip-content={dataAttrTooltipContent}
              ref={contentRef}
            >
              <span>{content}</span>
            </div>
          </motion.div>,
          tooltipRoot
        )}
      </div>
    )
  }
) as TooltipComponent

Tooltip.New = forwardRef<UseImperativeHandleRef, TooltipProps>((props, ref) => {
  return <Tooltip {...props} isNew ref={ref} />
})

Tooltip.Menu = forwardRef<UseImperativeHandleRef, TooltipProps>((props, ref) => {
  return <Tooltip {...props} isMenu ref={ref} />
})
